/**
 * Skill-gap board — derivation.
 *
 * Answers "where am I short a qualified head, and when", per work centre × job
 * role × 4-hour block. OT and flex can only be offered in 4h increments, so the
 * board is blocked in 4h and never hourly.
 *
 * REQUIRED comes from the day's labor standards: every phase carries a job
 * role, a head count and a position in the standard's timeline, so the demand
 * curve is whatever work is actually planned on the line. HAVE counts workers
 * holding that job role with an assignment overlapping the hour — minus anyone
 * confirmed out for that shift, who is on the card but not on the floor. Both sides are
 * summed in worker-hours and only then turned into heads, because comparing a
 * peak requirement against a trough of availability reports gaps that do not
 * exist.
 */
import { LaborStandard } from "../../../../../services/labor-standards-api";
import { RosterAssignment } from "../../../../../services/roster-assignments-api";
import { ManufacturingOrder } from "../../../production-plan/utils/calculations";
import { findStandardForOrder, isMachinePhase, placeOrderPhases } from "../../../utils/standard-matching";
import { dayStartMs, hoursInto, hoursIntoDate, localDay } from "../../../utils/time";
import { AvailabilityIndex, isConfirmedRow } from "./availability";

export interface ShiftBlock {
	/** Hour the block opens, as decimal hours from midnight. */
	start: number
	end: number
	label: string
}

/**
 * The plant runs two shifts. A third was modelled early on and removed — the
 * blocks below are the whole operating day, 05:00 to 22:00, so nothing should
 * ever be scheduled outside them.
 */
export type ShiftNumber = 1 | 2

export const SHIFT_BLOCKS: Record<ShiftNumber, ShiftBlock[]> = {
	1: [
		{ start: 5, end: 9, label: "05:00 – 09:00" },
		{ start: 9, end: 13, label: "09:00 – 13:00" },
	],
	2: [
		{ start: 14, end: 18, label: "14:00 – 18:00" },
		{ start: 18, end: 22, label: "18:00 – 22:00" },
	],
};

export const SHIFT_LABEL: Record<ShiftNumber, string> = {
	1: "1st shift",
	2: "2nd shift",
};

export interface GapCell {
	blockIndex: number
	block: ShiftBlock
	reqHours: number
	haveHours: number
	gapHours: number
	/** Heads to offer for the block — what the cell prints as "−2". */
	headsShort: number
	spareHours: number
}

export interface GapRow {
	workcenter: string
	jobRole: string
	jobRoleId: number | null
	cells: GapCell[]
	/** Total uncovered worker-hours across the row's blocks. */
	shortHours: number
	/** Qualified heads on the roster for this role, regardless of when. */
	qualifiedHeads: number
}

/**
 * Below this many worker-hours a block counts as covered. Rounding noise where a
 * phase clips a block boundary by a few minutes would otherwise ceiling to a
 * whole head — the board would show "−1" next to "0 worker-hours short" and
 * invite the planner to raise an OT request for four minutes of work.
 */
const MIN_GAP_HOURS = 0.5;

/** A required phase slot placed on the day's clock. */
interface RequiredSlot {
	workcenter: string
	jobRole: string
	jobRoleId: number | null
	start: number
	end: number
	heads: number
}

/** Overlap of [aStart,aEnd) and [bStart,bEnd) in hours. */
const overlap = (aStart: number, aEnd: number, bStart: number, bEnd: number): number =>
	Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));

export interface BuildGapBoardArgs {
	day: Date
	shift: ShiftNumber
	orders: ManufacturingOrder[]
	standards: LaborStandard[]
	assignments: RosterAssignment[]
	/** worker id -> the job roles they hold, from the skills/jobs data. */
	workerRoles: Map<string, Set<string>>
	drafts: Record<string, string>
	/** Who is out: their assignments supply no HAVE hours (a pending request still counts as present). */
	availability?: AvailabilityIndex
}

export const gapKey = (workcenter: string, jobRole: string, blockIndex: number): string =>
	`${workcenter}::${jobRole}::b${blockIndex}`;

export function buildGapBoard({
	day,
	shift,
	orders,
	standards,
	assignments,
	workerRoles,
	drafts,
	availability,
}: BuildGapBoardArgs): GapRow[] {
	const dayStart = dayStartMs(day);
	const dayIso = localDay(day);
	const blocks = SHIFT_BLOCKS[shift];
	const standardById = new Map(standards.map((s) => [s.id, s]));

	// ---- REQUIRED: every phase of every STEP on the day, placed on the clock.
	// placeOrderPhases mirrors the allocation engine: a single-operation step
	// takes its planned window (a 20 h press step covers both shifts), a
	// multi-operation recipe runs its operations in sequence. Batch headers
	// place nothing. A spanning step is placed in absolute time, so the hours
	// that fall on THIS day are whatever overlaps the day's blocks.
	const required: RequiredSlot[] = [];
	orders.forEach((order) => {
		const standard =
			(order.laborStandardId ? standardById.get(order.laborStandardId) : undefined) ??
			findStandardForOrder(order, standards);
		if (!standard) {
			return;
		}
		placeOrderPhases(order, standard).forEach((placed) => {
			// A machine phase holds the unit but needs nobody: no required hours.
			if (isMachinePhase(placed.phase)) {
				return;
			}
			required.push({
				workcenter: placed.workstation || order.workstation,
				jobRole: placed.phase.jobRole,
				jobRoleId: placed.phase.jobRoleId ?? null,
				start: hoursIntoDate(placed.start, dayStart),
				end: hoursIntoDate(placed.end, dayStart),
				heads: placed.phase.workersRequired,
			});
		});
	});

	// ---- HAVE: assignments, grouped by the work centre they staff — the
	// assignment's own (copied from the step) first, the order's as fallback.
	const orderWorkcenter = new Map(orders.map((o) => [o.id, o.workstation]));
	const have = assignments
		.filter((a) => {
			const row = availability?.lookup(String(a.workerId), a.shiftId ?? null, dayIso) ?? null;
			return !(row && isConfirmedRow(row));
		})
		.map((a) => ({
			workcenter: a.workstation || orderWorkcenter.get(a.workOrderId) || "",
			jobRole: a.jobRoleName,
			workerId: String(a.workerId),
			start: hoursInto(a.startTime, dayStart),
			end: hoursInto(a.endTime, dayStart),
		}))
		.filter((a) => a.workcenter);

	// Rows are work centre x job role — the unit the allocator gates on and the
	// unit an OT/flex offer is actually written against.
	const rowKeys = new Map<string, { workcenter: string; jobRole: string; jobRoleId: number | null }>();
	required.forEach((slot) => {
		rowKeys.set(`${slot.workcenter}::${slot.jobRole}`, {
			workcenter: slot.workcenter,
			jobRole: slot.jobRole,
			jobRoleId: slot.jobRoleId,
		});
	});

	const rows: GapRow[] = [];
	rowKeys.forEach(({ workcenter, jobRole, jobRoleId }) => {
		const cells: GapCell[] = blocks.map((block, blockIndex) => {
			// Worker-hours of demand this block, from the phases overlapping it.
			const reqHours = required
				.filter((s) => s.workcenter === workcenter && s.jobRole === jobRole)
				.reduce((total, s) => total + overlap(s.start, s.end, block.start, block.end) * s.heads, 0);

			// Worker-hours supplied by people who hold the role.
			const haveHours = have
				.filter(
					(a) =>
						a.workcenter === workcenter &&
						(a.jobRole === jobRole || workerRoles.get(a.workerId)?.has(jobRole))
				)
				.reduce((total, a) => total + overlap(a.start, a.end, block.start, block.end), 0);

			const rawGap = Math.max(reqHours - haveHours, 0);
			const gapHours = rawGap < MIN_GAP_HOURS ? 0 : rawGap;
			return {
				blockIndex,
				block,
				reqHours,
				haveHours,
				gapHours,
				headsShort: Math.ceil(gapHours / 4),
				spareHours: Math.max(haveHours - reqHours, 0),
			};
		});

		// Drafted cover closes the gap on the board without touching the plan.
		const shortHours = cells.reduce(
			(total, cell) =>
				total + (drafts[gapKey(workcenter, jobRole, cell.blockIndex)] ? 0 : cell.gapHours),
			0
		);

		const qualifiedHeads = new Set(
			have.filter((a) => a.jobRole === jobRole || workerRoles.get(a.workerId)?.has(jobRole)).map((a) => a.workerId)
		).size;

		rows.push({ workcenter, jobRole, jobRoleId, cells, shortHours, qualifiedHeads });
	});

	return rows.sort(
		(a, b) => a.workcenter.localeCompare(b.workcenter) || a.jobRole.localeCompare(b.jobRole)
	);
}

/** Board totals, used by the footer strip and the coverage metric. */
export function summariseGaps(rows: GapRow[], drafts: Record<string, string>) {
	let shortBlocks = 0;
	let uncoveredHours = 0;
	let requiredHours = 0;

	rows.forEach((row) => {
		row.cells.forEach((cell) => {
			requiredHours += cell.reqHours;
			if (cell.gapHours > 0 && !drafts[gapKey(row.workcenter, row.jobRole, cell.blockIndex)]) {
				shortBlocks += 1;
				uncoveredHours += cell.gapHours;
			}
		});
	});

	return {
		shortBlocks,
		uncoveredHours,
		requiredHours,
		// The headline metric is computed HERE so it can never disagree with the
		// board's own footer.
		coveredPct: requiredHours > 0 ? Math.round(((requiredHours - uncoveredHours) / requiredHours) * 100) : 100,
	};
}
